import { CircularProgress, DialogActions, makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import foundersAPI from '../../api/founders';
import mentorsAPI from '../../api/mentors';
import { Role } from '../../api/user/Role';
import venturesAPI from '../../api/ventures';
import { UserContext } from '../../contexts/user-context';
import { Pages } from '../../router/constants';
import AlertState from './alert-state';
import Button from './button';
import ModalV2 from './modal-v2';
import Text from './text';
import TextField from './text-field';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  actions: {
    padding: '32px 0 0',
  },
  textField: {
    marginTop: '16px',
  },
});

interface DeleteButtonProps {
  type: 'venture' | 'mentor' | 'founder';
  id: string;
}

interface DeleteModalProps extends DeleteButtonProps {
  open: boolean;
  onClose: () => void;
}

const DELETE_VERIFICATION_TEXT = 'Delete';

export function DeleteModal({ open, type, id, onClose }: DeleteModalProps) {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToAction, hasRole } = useContext(UserContext);

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = useCallback(async () => {
    try {
      if (inputValue !== DELETE_VERIFICATION_TEXT) {
        enqueueSnackbar(
          'Please type "Delete" in the edit box below and click Confirm button',
          { variant: 'error' },
        );
        return;
      }

      setIsLoading(true);

      switch (type) {
        case 'venture':
          await venturesAPI.delete(id);
          history.push(
            hasAccessToAction('venture.filters')
              ? hasRole(Role.Mentor)
                ? `${Pages.VENTURES}?mentorFilter=my-ventures`
                : Pages.ACTIVE_VENTURES
              : Pages.VENTURES,
          );
          break;
        case 'mentor':
          await mentorsAPI.delete(id);
          history.push(Pages.ACTIVE_MENTORS);
          break;
        case 'founder':
          await foundersAPI.delete(id);
          history.push(
            hasAccessToAction('founder.filters.status')
              ? Pages.ACTIVE_FOUNDERS
              : Pages.FOUNDERS,
          );
          break;
      }

      onClose();
      enqueueSnackbar('The item was successfully deleted!', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(
        'An error occurred when deleting the item. Please try again.',
        {
          variant: 'error',
        },
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    id,
    type,
    inputValue,
    history,
    onClose,
    enqueueSnackbar,
    hasAccessToAction,
    hasRole,
  ]);

  return (
    <ModalV2
      width='480px'
      open={open}
      onClose={onClose}
      titleProps={{ title: 'Destructive Action.' }}>
      <AlertState type='error'>
        <Text variant='normal'>
          Please note that you will delete the information without an ability to
          restore it.
        </Text>
      </AlertState>
      <Text variant='normal'>
        In order to proceed type <b>'{DELETE_VERIFICATION_TEXT}'</b> in the edit
        box below and click Confirm button.
      </Text>
      <TextField
        type='text'
        label={`Please type '${DELETE_VERIFICATION_TEXT}'`}
        error={false}
        value={inputValue}
        disabled={isLoading}
        onChange={(e) => setInputValue(e.target.value)}
        className={classes.textField}
      />
      <DialogActions className={classes.actions}>
        <Button
          variant='outlined'
          onClick={onClose}
          data-testid='button-cancel'>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={isLoading || inputValue !== DELETE_VERIFICATION_TEXT}
          data-testid='button-success'>
          {isLoading ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            'Delete'
          )}
        </Button>
      </DialogActions>
    </ModalV2>
  );
}

function DeleteButton({ type, id }: DeleteButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant='contained'
        onClick={() => setOpen(true)}
        data-testid='button-cancel'>
        Delete
      </Button>
      <DeleteModal
        type={type}
        id={id}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export default DeleteButton;
